// The recorder engine's browser half: the microphone, MediaRecorder, the
// level meter, the screen wake lock, Web Locks, timers and the api. One
// engine per page (`recorder()`), created on first use.

import { finishRecording, getMeeting, getRecordingUpload, postRecorderDiag, putChunk, startRecording } from "@/api/meetingRecordings";
import { nativeCallAudioAvailable } from "@/native/desktopCallAudio";
import { captureCallSound } from "./callSound";
import { captureNativeCallSound } from "./nativeCallSound";
import { RecorderEngine } from "./recorderEngine";
import { PUT_TIMEOUT_MS } from "./recorderModel";
import { deviceStore } from "./recorderStore";
import type { EngineDeps, LockDeps, MediaDeps, RecorderLike, StreamLike } from "./recorderTypes";

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus", "audio/webm", "audio/mp4;codecs=mp4a.40.2", "audio/mp4", "audio/ogg;codecs=opus",
];

export { isApple, isStandalone, micErrorMessage } from "./micWords";

export const recordingSupported = () =>
  typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== "undefined";

const browserMedia: MediaDeps = {
  supported: recordingSupported,
  getUserMedia: (o) =>
    o?.callSound
      ? (nativeCallAudioAvailable() ? captureNativeCallSound() : captureCallSound())
      : (navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } }) as Promise<unknown> as Promise<StreamLike>),
  pickMime() {
    for (const m of MIME_CANDIDATES) if (MediaRecorder.isTypeSupported?.(m)) return m;
    return "";
  },
  createRecorder(stream, mime) {
    const opts: MediaRecorderOptions = mime ? { mimeType: mime, audioBitsPerSecond: 48_000 } : { audioBitsPerSecond: 48_000 };
    return new MediaRecorder(stream as unknown as MediaStream, opts) as unknown as RecorderLike;
  },
  meter(stream, onLevel) {
    // The Mac app's call mix meters itself: no second AudioContext on its stream.
    const own = stream.callSound?.meter?.(onLevel);
    if (own) return own;
    try {
      const ctx = new AudioContext();
      const an = ctx.createAnalyser();
      an.fftSize = 512;
      ctx.createMediaStreamSource(stream as unknown as MediaStream).connect(an);
      const buf = new Uint8Array(an.fftSize);
      const t = window.setInterval(() => {
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        onLevel(Math.min(1, Math.sqrt(sum / buf.length) * 4));
      }, 100);
      return () => {
        window.clearInterval(t);
        void ctx.close().catch(() => undefined);
      };
    } catch {
      return () => undefined; // the meter is cosmetic
    }
  },
  wakeLock() {
    let sentinel: WakeLockSentinel | null = null;
    let released = false;
    if ("wakeLock" in navigator) {
      navigator.wakeLock
        .request("screen")
        .then((s) => {
          if (released) void s.release().catch(() => undefined);
          else sentinel = s;
        })
        .catch(() => undefined); // not fatal: the person can keep the screen on
    }
    return () => {
      released = true;
      void sentinel?.release().catch(() => undefined);
    };
  },
  async micGranted() {
    try {
      const p = navigator.permissions as unknown as { query?: (d: { name: string }) => Promise<{ state: string }> };
      return (await p?.query?.({ name: "microphone" }))?.state === "granted";
    } catch {
      return false;
    }
  },
};

type WebLocks = {
  request(name: string, opts: { ifAvailable: boolean }, cb: (lock: unknown) => Promise<void> | boolean): Promise<unknown>;
};

const browserLocks = (): LockDeps => {
  const locks = (typeof navigator !== "undefined" ? (navigator as unknown as { locks?: WebLocks }).locks : undefined) ?? null;
  return {
    available: !!locks,
    hold: (name) =>
      new Promise((resolve) => {
        if (!locks) return resolve(() => undefined);
        locks
          .request(name, { ifAvailable: true }, (lock) => {
            if (!lock) {
              resolve(null);
              return false;
            }
            return new Promise<void>((release) => resolve(() => release()));
          })
          .catch(() => resolve(() => undefined));
      }),
  };
};

/** An abort after PUT_TIMEOUT_MS, where the browser can (older Safari has no AbortSignal.timeout). */
function timeout(): AbortSignal | undefined {
  try {
    return AbortSignal.timeout?.(PUT_TIMEOUT_MS);
  } catch {
    return undefined;
  }
}

const uuid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID().replace(/-/g, "")
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

export function browserDeps(): EngineDeps {
  return {
    media: browserMedia,
    // Every call gives up after PUT_TIMEOUT_MS (an answer that never comes is "no answer": tried again), so one
    // hung request never holds a take's queue.
    api: {
      start: (c, body) => startRecording(c, body, timeout()),
      put: (c, m, u, i, blob, o) => putChunk(c, m, u, i, blob, { keepalive: o.keepalive, timeoutMs: o.keepalive ? undefined : PUT_TIMEOUT_MS }),
      finish: (c, m, u, body) => finishRecording(c, m, u, body, timeout()),
      uploadState: (c, m, u) => getRecordingUpload(c, m, u, timeout()),
      meeting: (c, m) => getMeeting(c, m, timeout()),
      diag: (c, entry) => postRecorderDiag(c, entry),
    },
    clock: {
      now: () => Date.now(),
      setTimeout: (fn, ms) => window.setTimeout(fn, ms),
      clearTimeout: (id) => window.clearTimeout(id),
      setInterval: (fn, ms) => window.setInterval(fn, ms),
      clearInterval: (id) => window.clearInterval(id),
    },
    locks: browserLocks(),
    store: deviceStore,
    uuid,
    tabId: uuid(),
  };
}

let engine: RecorderEngine | null = null;

/** This page's recorder. */
export function recorder(): RecorderEngine {
  engine ??= new RecorderEngine(browserDeps());
  return engine;
}
